import type { SliceComponentType } from "@prismicio/react";
import { asText } from "@prismicio/helpers";
import { SliceZoneC } from "@commercekit/prismic";
import { components } from "~/lib/prismic";
import type { HTMLComponent } from "../../../../../libs/react/src/lib/types";
import type {
  ProductsAndCustomerContext,
  WovarCustomer,
} from "@commercekit/api";
import WvLink from "../atoms/WvLink";
import { useLocalePath } from "@commercekit/remix/lib/hooks/useLocale";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { useWvViewItemList } from "~/hooks/useDataLayer";
import { useEffect, useMemo } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import { useLocation } from "@remix-run/react";
import type { BlogContentPageResultType } from "~/routes-server/$slug/types";

const WvBlogContentPage: HTMLComponent<{
  blogContentPageResult: BlogContentPageResultType;
}> = ({ blogContentPageResult }) => {
  const localePath = useLocalePath();
  const { $pt } = usePrismicLabels();
  const gtmViewItemList = useWvViewItemList();
  const isHydrated = useHydrated();
  const location = useLocation();

  useMemo(() => {
    if (!blogContentPageResult?.popularProducts?.length) return null;
    gtmViewItemList(blogContentPageResult?.popularProducts);
  }, [gtmViewItemList, blogContentPageResult?.popularProducts]);

  // Inject BlogPosting JSON-LD schema
  useEffect(() => {
    if (!isHydrated || !blogContentPageResult?.blogContent) return;

    // Remove any existing blog post schema
    const existingScript = document.querySelector(
      "script[data-blog-post-schema]"
    );
    if (existingScript) {
      existingScript.remove();
    }

    const blogContent = blogContentPageResult.blogContent;
    const headline = asText(blogContent.data?.title);
    const description = asText(blogContent.data?.short_introduction);
    const datePublished =
      blogContent.data?.date_posted != null
        ? String(blogContent.data.date_posted)
        : undefined;
    const imageUrl = blogContent.data?.image?.url;

    // Find author from slices
    const authorSlice = blogContent.data?.slices?.find(
      (s: { slice_type: string; primary?: unknown }) =>
        s.slice_type === "blog_content_author" && "primary" in s
    );
    const authorName =
      authorSlice && "primary" in authorSlice && authorSlice.primary
        ? asText(
            (authorSlice.primary as { author_name?: Parameters<typeof asText>[0] })
              .author_name
          )
        : undefined;

    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: headline || undefined,
      description: description || undefined,
      url: window.location.href,
      datePublished,
    };

    if (imageUrl) {
      schema.image = imageUrl;
    }

    if (authorName) {
      schema.author = {
        "@type": "Person",
        name: authorName,
      };
    }

    const blogPostScript = document.createElement("script");
    blogPostScript.type = "application/ld+json";
    blogPostScript.setAttribute("data-blog-post-schema", "");
    blogPostScript.innerHTML = JSON.stringify(schema);
    document.head.appendChild(blogPostScript);

    // Cleanup on unmount or when blog content changes
    return () => {
      const scriptToRemove = document.querySelector(
        "script[data-blog-post-schema]"
      );
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [isHydrated, blogContentPageResult?.blogContent, location.pathname]);

  const productsAndCustomerContext: ProductsAndCustomerContext = {
    products: blogContentPageResult?.popularProducts,
    customer: blogContentPageResult.customer as WovarCustomer,
  };
  return (
    <section className="subpage">
      <div className="container">
        {blogContentPageResult?.blogContent.data.show_breadcrumbs ? (
          <div className="breadcrumbs py-3 mb-3">
            <WvLink
              to={localePath("/")}
              className="news first"
              data-icon-before=""
            >
              {$pt("breadcrumbs_label_home")}
            </WvLink>
            <i data-icon-after=""></i>
            <WvLink
              to={localePath("/blog/")}
              className="news mobile"
              data-icon-before=""
            >
              {$pt("breadcrumbs_label_blog")}
            </WvLink>

            <i data-icon-after=""></i>
            <span className="news active" data-icon-before="">
              <span className=""></span>
              {asText(blogContentPageResult?.blogContent.data.title)}
            </span>
          </div>
        ) : (
          <div className="d-block py-3"></div>
        )}
        <div className="row blogpage">
          {blogContentPageResult?.blogContent?.data.slices &&
          blogContentPageResult?.blogContent?.data.slices.length ? (
            <SliceZoneC
              slices={blogContentPageResult?.blogContent?.data.slices}
              context={productsAndCustomerContext}
              components={components as Record<string, SliceComponentType>}
              defaultComponent={({ slice }) => (
                <div className="hidden">{slice.slice_type}</div>
              )}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};
export default WvBlogContentPage;
